/**
 * Assegna / svincola giocatori — scheda "Svincola / sposta / modifica"
 * (Andrea 2026-10-09): l'admin vede la rosa di ogni squadra e puo'
 * - togliere un giocatore (svincolo) con un rimborso a scelta: valore TM,
 *   prezzo pagato, zero o a mano;
 * - mandarlo al form di assegnazione per spostarlo in un'altra squadra;
 * - correggere il prezzo d'acquisto ("se qualcuno sbaglia a comprare"):
 *   la differenza va o torna nel budget.
 * Serve a correggere gli errori; solo a mercato aperto.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRosa, type RosaResponse } from "../../hooks/useFase2";
import { api } from "../../lib/api";
import { SquadraSelector } from "../common/SquadraSelector";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { sigleRuoli } from "../common/nomenclatura";
import { tr } from "../../i18n";

type Riga = RosaResponse["items"][number];
type Pannello = { tipo: "svincola" | "prezzo"; giocatoreId: string } | null;

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? "errore";
}

function num(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function RoseSvincoloTab({
  stagioneId,
  onSposta,
}: {
  stagioneId: string;
  /** "manda" il giocatore al form di assegnazione per spostarlo altrove */
  onSposta: (g: { id: string; nome: string; valore_tm_milioni: number | null }) => void;
}) {
  const qc = useQueryClient();
  const [squadraId, setSquadraId] = useState("");
  const { data: rosa, isLoading } = useRosa(stagioneId, squadraId || null);
  const [pannello, setPannello] = useState<Pannello>(null);
  const [rimborsoStr, setRimborsoStr] = useState("");
  const [prezzoStr, setPrezzoStr] = useState("");
  const [motivo, setMotivo] = useState("");
  const [esito, setEsito] = useState<string | null>(null);

  const chiudi = () => {
    setPannello(null);
    setMotivo("");
  };
  const aggiorna = () => {
    qc.invalidateQueries({ queryKey: ["rosa", stagioneId, squadraId] });
    qc.invalidateQueries({ queryKey: ["giocatori-search"] });
    qc.invalidateQueries({ queryKey: ["budget"] });
  };

  const svincola = useMutation({
    mutationFn: async (r: Riga) => {
      const rimborso = Number(rimborsoStr || 0);
      if (!Number.isFinite(rimborso) || rimborso < 0) throw new Error(tr("Rimborso non valido"));
      await api.post("/admin/asta-svincolati/svincola", {
        squadra_id: squadraId,
        giocatore_id: r.giocatore_id,
        stagione_id: stagioneId,
        rimborso_milioni: rimborso,
        motivo: motivo.trim() || null,
      });
      return { nome: r.nome, rimborso };
    },
    onSuccess: (d) => {
      setEsito(
        d.rimborso > 0
          ? tr("✓ {{v1}} svincolato, +{{v2}}M al budget", {
              v1: d.nome,
              v2: d.rimborso.toFixed(2),
            })
          : tr("✓ {{v1}} svincolato", { v1: d.nome }),
      );
      chiudi();
      aggiorna();
    },
    onError: (e) => setEsito(tr("Errore: {{v1}}", { v1: errMsg(e) })),
  });

  const cambiaPrezzo = useMutation({
    mutationFn: async (r: Riga) => {
      const prezzo = Number(prezzoStr);
      if (prezzoStr.trim() === "" || !Number.isFinite(prezzo) || prezzo < 0)
        throw new Error(tr("Prezzo non valido (deve essere >= 0)"));
      const res = await api.post<{
        giocatore_nome: string;
        prezzo_precedente: string;
        prezzo_nuovo: string;
        differenza_budget: string;
      }>("/admin/asta-svincolati/modifica-prezzo", {
        squadra_id: squadraId,
        giocatore_id: r.giocatore_id,
        stagione_id: stagioneId,
        nuovo_prezzo_milioni: prezzo,
        motivo: motivo.trim() || null,
      });
      return res.data;
    },
    onSuccess: (d) => {
      const diff = Number(d.differenza_budget);
      setEsito(
        tr("✓ {{v1}}: prezzo {{v2}} → {{v3}}, budget {{v4}}M", {
          v1: d.giocatore_nome,
          v2: Number(d.prezzo_precedente).toFixed(1),
          v3: Number(d.prezzo_nuovo).toFixed(1),
          v4: (diff > 0 ? "+" : "") + diff.toFixed(2),
        }),
      );
      chiudi();
      aggiorna();
    },
    onError: (e) => setEsito(tr("Errore: {{v1}}", { v1: errMsg(e) })),
  });

  const apriSvincolo = (r: Riga) => {
    setEsito(null);
    setPannello({ tipo: "svincola", giocatoreId: r.giocatore_id });
    const tm = num(r.valore_tm_milioni);
    const pagato = num(r.prezzo_acquisto_milioni);
    setRimborsoStr(String(tm ?? pagato ?? 0));
  };

  const apriPrezzo = (r: Riga) => {
    setEsito(null);
    setPannello({ tipo: "prezzo", giocatoreId: r.giocatore_id });
    const pagato = num(r.prezzo_acquisto_milioni);
    setPrezzoStr(pagato === null ? "" : String(pagato));
  };

  const attivo = (r: Riga, tipo: "svincola" | "prezzo") =>
    pannello?.tipo === tipo && pannello.giocatoreId === r.giocatore_id;

  return (
    <div className="card space-y-3">
      <p className="text-xs text-fg-muted">
        {tr(
          "Scegli una squadra: per ogni giocatore vedi prezzo pagato e valore TM. «Svincola» lo toglie dalla rosa e rimette nel budget la cifra che decidi (TM, pagato, zero o a mano); «Sposta» lo manda al form di assegnazione per metterlo in un'altra squadra; «Modifica» corregge il prezzo d'acquisto e la differenza va o torna nel budget.",
        )}
      </p>
      <SquadraSelector
        value={squadraId}
        onChange={(v) => {
          setSquadraId(v);
          setPannello(null);
          setEsito(null);
        }}
        label={tr("Squadra")}
        autoSelectMine={false}
      />
      {esito && (
        <div
          className={`text-sm rounded p-2 ${esito.startsWith("✓") ? "text-success bg-success/10" : "text-danger bg-danger/10"}`}
        >
          {esito}
        </div>
      )}
      {squadraId && isLoading && (
        <div className="text-sm text-fg-muted">{tr("Carico la rosa…")}</div>
      )}
      {rosa && (
        <div className="divide-y divide-primary/15">
          <div className="text-[11px] text-fg-muted pb-2">
            {tr("{{v1}} giocatori · {{v2}} in lista · {{v3}} provvisori", {
              v1: rosa.totale,
              v2: rosa.ufficiali,
              v3: rosa.provvisorie,
            })}
          </div>
          {rosa.items.map((r) => {
            const tm = num(r.valore_tm_milioni);
            const pagato = num(r.prezzo_acquisto_milioni);
            const inPrestito = !!r.tipo_possesso && r.tipo_possesso !== "proprieta";
            return (
              <div key={r.iscrizione_id} className="py-2">
                <div className="flex items-center gap-2">
                  <PlayerAvatar nome={r.nome} size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-fg truncate">{r.nome}</div>
                    <div className="text-[10px] text-fg-muted">
                      {r.ruoli_mantra ? sigleRuoli(r.ruoli_mantra) : r.ruolo_classic}
                      {r.club_serie_a ? ` · ${r.club_serie_a}` : ""}
                      {inPrestito ? ` · ${r.tipo_possesso}` : ""}
                    </div>
                  </div>
                  <div className="text-right font-mono text-[11px] tabular-nums shrink-0">
                    <div className="text-fg-muted">
                      {"pagato"}{" "}
                      <span className="text-fg">{pagato === null ? "—" : pagato.toFixed(1)}</span>
                    </div>
                    <div className="text-fg-muted">
                      TM <span className="text-accent">{tm === null ? "—" : tm.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      className="btn-ghost text-[11px] px-2 py-1"
                      title={tr("Spostalo in un'altra squadra")}
                      onClick={() =>
                        onSposta({ id: r.giocatore_id, nome: r.nome, valore_tm_milioni: tm })
                      }
                    >
                      {tr("Sposta")}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-[11px] px-2 py-1 border-accent/60 text-accent hover:bg-accent/10"
                      disabled={inPrestito}
                      title={
                        inPrestito
                          ? tr("In prestito: si gestisce dai prestiti")
                          : tr("Correggi il prezzo d'acquisto")
                      }
                      onClick={() => (attivo(r, "prezzo") ? chiudi() : apriPrezzo(r))}
                    >
                      {tr("Modifica")}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-[11px] px-2 py-1 border-danger/40 text-danger hover:bg-danger/10"
                      disabled={inPrestito}
                      title={
                        inPrestito
                          ? tr("In prestito: si gestisce dai prestiti")
                          : tr("Toglilo dalla rosa")
                      }
                      onClick={() => (attivo(r, "svincola") ? chiudi() : apriSvincolo(r))}
                    >
                      {tr("Svincola")}
                    </button>
                  </div>
                </div>
                {attivo(r, "prezzo") && (
                  <div className="mt-2 ml-9 rounded-lg border border-accent/40 bg-accent/5 p-3 space-y-2">
                    <div className="text-xs text-fg">
                      {tr("Nuovo prezzo d'acquisto di")} <b>{r.nome}</b> {tr("(fantamilioni)")}
                      {pagato !== null && (
                        <span className="text-fg-muted">
                          {" "}
                          · {tr("ora {{v1}}", { v1: pagato.toFixed(1) })}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        min="0"
                        value={prezzoStr}
                        onChange={(e) => setPrezzoStr(e.target.value)}
                        className="w-28 px-2 py-1 bg-bg border border-primary-soft/50 rounded text-sm text-fg"
                      />
                      {tm !== null && (
                        <button
                          type="button"
                          className="btn-ghost text-[11px] px-2 py-1"
                          onClick={() => setPrezzoStr(String(tm))}
                        >
                          TM {tm.toFixed(1)}
                        </button>
                      )}
                      {(() => {
                        const nuovo = Number(prezzoStr);
                        if (prezzoStr.trim() === "" || !Number.isFinite(nuovo)) return null;
                        const diff = (pagato ?? 0) - nuovo;
                        if (diff === 0) return null;
                        return (
                          <span
                            className={`text-[11px] ${diff < 0 ? "text-danger" : "text-success"}`}
                          >
                            {tr("budget {{v1}}M", { v1: (diff > 0 ? "+" : "") + diff.toFixed(2) })}
                          </span>
                        );
                      })()}
                    </div>
                    <input
                      type="text"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder={tr("Motivo (facoltativo, finisce nello storico budget)")}
                      className="w-full px-2 py-1 bg-bg border border-primary-soft/50 rounded text-sm text-fg"
                    />
                    <div className="flex gap-2 justify-end">
                      <button type="button" className="btn-ghost text-xs" onClick={chiudi}>
                        {tr("Annulla")}
                      </button>
                      <button
                        type="button"
                        className="btn-primary text-xs disabled:opacity-50"
                        disabled={cambiaPrezzo.isPending}
                        onClick={() => cambiaPrezzo.mutate(r)}
                      >
                        {cambiaPrezzo.isPending ? tr("Salvataggio…") : tr("Salva prezzo")}
                      </button>
                    </div>
                  </div>
                )}
                {attivo(r, "svincola") && (
                  <div className="mt-2 ml-9 rounded-lg border border-danger/30 bg-danger/5 p-3 space-y-2">
                    <div className="text-xs text-fg">
                      {tr("Rimborso al budget di")} <b>{r.nome}</b> {tr("(fantamilioni)")}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        min="0"
                        value={rimborsoStr}
                        onChange={(e) => setRimborsoStr(e.target.value)}
                        className="w-28 px-2 py-1 bg-bg border border-primary-soft/50 rounded text-sm text-fg"
                      />
                      {tm !== null && (
                        <button
                          type="button"
                          className="btn-ghost text-[11px] px-2 py-1"
                          onClick={() => setRimborsoStr(String(tm))}
                        >
                          TM {tm.toFixed(1)}
                        </button>
                      )}
                      {pagato !== null && (
                        <button
                          type="button"
                          className="btn-ghost text-[11px] px-2 py-1"
                          onClick={() => setRimborsoStr(String(pagato))}
                        >
                          {tr("Pagato")} {pagato.toFixed(1)}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-ghost text-[11px] px-2 py-1"
                        onClick={() => setRimborsoStr("0")}
                      >
                        {tr("Zero")}
                      </button>
                    </div>
                    <input
                      type="text"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder={tr("Motivo (facoltativo, finisce nello storico budget)")}
                      className="w-full px-2 py-1 bg-bg border border-primary-soft/50 rounded text-sm text-fg"
                    />
                    <div className="flex gap-2 justify-end">
                      <button type="button" className="btn-ghost text-xs" onClick={chiudi}>
                        {tr("Annulla")}
                      </button>
                      <button
                        type="button"
                        className="btn-danger text-xs disabled:opacity-50"
                        disabled={svincola.isPending}
                        onClick={() => {
                          if (
                            !confirm(
                              tr("Svincolare {{v1}}? Esce dalla rosa e dal campo.", { v1: r.nome }),
                            )
                          )
                            return;
                          svincola.mutate(r);
                        }}
                      >
                        {svincola.isPending ? tr("Svincolo…") : tr("Conferma svincolo")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
